<template>
  <div class="past-months-graph-card card-box">
    <div class="title">{{this.formattedTitle}}</div>
      <svg class=past-months-graph>
        <polygon class=x-axis-arrow points="0 75.5,7 79.5,7 71.5"/>
        <line class="x-axis" x1="1" :y1="xAxisHeight" :x2="width + 2" :y2="xAxisHeight"/>
        <text class="x-axis-zero" :x="width + 12" :y="xAxisHeight + 3">0</text>
        <g v-for="budget of presentedBudgets" :key="budget.budgetDate.date">
          <template v-if="currentMonthLine(budget)">
            <line class="month-line" :class="{ 'positive-line': isMonthBalancePositive, 'negative-line': isMonthBalanceNegative }"
                  :x1="monthLineX(budget)" y1="0" :x2="monthLineX(budget)" :y2="height" />
            <line class="month-line-cursor"
                  :class="{ 'positive-line': isMonthBalancePositive, 'negative-line': isMonthBalanceNegative }"
                  :x1="monthLineX(budget)" :y1="height" :x2="monthLineX(budget)" :y2="height + 4" />
            <text class="month-caption bold-caption"
                  :class="{ 'positive-caption': isMonthBalancePositive, 'negative-caption': isMonthBalanceNegative }"
                  :x="monthLineX(budget)" :y="height" :dx="monthNamePixelLength(budget) / 2" :dy="19">{{ formatMonthName(budget) }}</text>
          </template>
          <template v-else>
            <line class="month-line" :x1="monthLineX(budget)" y1="0"
                  :x2="monthLineX(budget)" :y2="height + 1" />
            <line :class="{ 'month-line-cursor': !emptyMonth(budget), 'empty-month-line-cursor': emptyMonth(budget) }"
                  :x1="monthLineX(budget)" :y1="height" :x2="monthLineX(budget)" :y2="height + 4" />
            <text :class="{ 'month-caption': !emptyMonth(budget), 'empty-month-caption': emptyMonth(budget) }"
                  :x="monthLineX(budget)" :y="height" :dx="monthNamePixelLength(budget) / 2" :dy="19">{{ formatMonthName(budget) }}</text>
          </template>

          <template v-if="positiveMonth(budget)">
            <rect class="positive-bar" :x="monthLineX(budget) - 8" :y="xAxisHeight - relativeAmountHeight(budget)"
                  width="16" :height="relativeAmountHeight(budget)"/>
            <rect class="caption-background" :x="monthLineX(budget) - 8" :y="xAxisHeight - relativeAmountHeight(budget) - 12"
                  width="16" height="9"/>
            <text class="month-caption" :class="{ 'positive-caption': positiveMonth(budget),
                  'negative-caption': !positiveMonth(budget), 'bold-caption': currentMonthLine(budget)}"
                  :x="monthLineX(budget)" :y="xAxisHeight - relativeAmountHeight(budget)"
                  :dx="amountPixelLength(budget) / 2" :dy="-4">{{ formatAmount(budget) }}</text>
          </template>
          <template v-else>
            <rect class="negative-bar" :x="monthLineX(budget) - 8" :y="xAxisHeight"
                  width="16" :height="relativeAmountHeight(budget)"/>
            <rect v-if="!emptyMonth(budget)" class="caption-background" :x="monthLineX(budget) - 8"
                  :y="xAxisHeight + relativeAmountHeight(budget) + 3" width="16" height="9"/>
            <text class="month-caption" :class="{ 'positive-caption': positiveMonth(budget),
                  'negative-caption': !positiveMonth(budget), 'bold-caption':  currentMonthLine(budget) }"
                  :x="monthLineX(budget)" :y="xAxisHeight + relativeAmountHeight(budget)"
                  :dx="amountPixelLength(budget) / 2" :dy="11">{{ formatAmount(budget) }}</text>
          </template>
        </g>
      </svg>
  </div>
</template>

<script>
import _ from 'lodash';
import moment from 'moment';
import { mapGetters, mapState } from 'vuex';

import moneyUtils from '../../../../../../../../utils/money';

const MONTHS_GAP = 20;
const MONTHS_LENGTH = 5;
const NARROW_LETTERS = [
  'ו',
  'י',
  '׳',
];
const NARROW_DIGITS = [
  '1',
];

function _placeHoldEmptyBudgets(budgetTotals) {
  let fullBudgetTotals = [...budgetTotals];
  if (budgetTotals.length < MONTHS_LENGTH) {
    const oldestBudget = _.last(_.map(budgetTotals, budgetTotal => budgetTotal.budgetDate));
    for (let i = 1; i <= MONTHS_LENGTH - budgetTotals.length; i++) {
      fullBudgetTotals = _.concat(fullBudgetTotals, {
        budgetDate: moment(oldestBudget).subtract(i, 'month').format('YYYY-MM'),
      });
    }
  }
  return fullBudgetTotals;
}

export default {
  name: 'PastMonthsGraph',
  components: {
  },
  computed: {
    ...mapGetters('cashflowView', ['monthName', 'balance', 'budgetsTotals']),
    ...mapState('cashflowView', ['presentedBudgetDate']),
    formattedTitle() {
      return `${this.monthName} ביחס לחודשים אחרים`;
    },
    width() {
      return 274;
    },
    height() {
      return 143;
    },
    xAxisHeight() {
      return (this.height / 2) + 4;
    },
    months() {
      return _.map(this.presentedBudgets, budgetTotal => this.formatMonthName(budgetTotal.budgetDate));
    },
    monthsGap() {
      return (this.width - (MONTHS_GAP * 2)) / (this.months.length - 1);
    },
    presentedBudgets() {
      const slicedBudgetTotals = _.slice(this.budgetsTotals, 1, MONTHS_LENGTH + 1);
      return _placeHoldEmptyBudgets(slicedBudgetTotals);
    },
    amountsMax() {
      const allAmounts = _.map(this.presentedBudgets.map(budget => Math.abs(budget.total)));
      return _.maxBy(allAmounts);
    },
    isMonthBalancePositive() {
      return this.balance > 0;
    },
    isMonthBalanceEven() {
      return this.balance === 0;
    },
    isMonthBalanceNegative() {
      return this.balance < 0;
    },
  },
  methods: {
    monthIndex(budgetDate) {
      return _.indexOf(_.map(this.presentedBudgets, budget => budget.budgetDate), budgetDate);
    },
    monthLineX(budget) {
      return (this.monthsGap * this.monthIndex(budget.budgetDate)) + MONTHS_GAP;
    },
    formatMonthName(budget) {
      return moment(budget.budgetDate).locale('he').format('MMM');
    },
    formatAmount(budget) {
      if (budget.total) {
        const mark = budget.total > 0 ? '+' : (budget.total < 0 ? '-' : ''); // eslint-disable-line
        return `${moneyUtils.formatAmountWithFraction(budget.total, 0)} ${mark}`;
      }
      return '';
    },
    positiveMonth(budget) {
      return budget.total ? budget.total >= 0 : false;
    },
    emptyMonth(budget) {
      return !budget.total;
    },
    monthNamePixelLength(budget) {
      const monthName = this.formatMonthName(budget);
      let pixleLength = 0;
      for (let i = 0; i < monthName.length; i++) {
        pixleLength += NARROW_LETTERS.includes(monthName.charAt(i)) ? 3 : 5;
      }
      return pixleLength;
    },
    amountPixelLength(budget) {
      const amountStr = `${moneyUtils.formatAmountWithFraction(budget.total, 0)}`;
      let pixleLength = 0;
      for (let i = 0; i < amountStr.length; i++) {
        pixleLength += NARROW_DIGITS.includes(amountStr.charAt(i)) ? 3 : 5;
      }
      return pixleLength;
    },
    currentMonthLine(budget) {
      return this.presentedBudgetDate === budget.budgetDate;
    },
    relativeAmountHeight(budget) {
      return this.amountsMax ? this.calcRelativeAmountHeight(budget) : 0;
    },
    calcRelativeAmountHeight(budget) {
      const budgetTotal = Math.abs(budget.total || 0);
      const relativeTotal = budgetTotal / this.amountsMax;
      return (this.height / 2) * relativeTotal * 0.6;
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '../../../../../../../../../node_modules/base-ui/src/scss/riseup-colors';
  @import '../../../../../../../../scss/mixins';

  .past-months-graph-card {
    margin-bottom: 16px;
    padding: 8px 18px 8px;
    position: relative;
    height: 240px;

    .title {
      font-family: GretaSans;
      font-size: 24px;
      font-weight: bold;
      line-height: 1.00em;
      margin: 4px 0 32px;
    }

    .past-months-graph {
      font-family: SimplerPro;
      width: 290px;
      height: 180px;
      position: absolute;
      top: 49%;
      left: 60%;
      margin: -58px 0 0 -170px;
      overflow: visible;

      .x-axis {
        stroke: $riseup_gray_0;
        stroke-width: 1px;
      }

      .x-axis-arrow {
        fill: $riseup_gray_0;
        stroke-width: 0px;
      }

      .x-axis-zero {
        font-size: 10px;
        fill: $riseup_black;
      }

      .month-line {
        stroke: $riseup_gray_0;
        stroke-width: 2px;
        stroke-dasharray: 2,3;
      }

      .month-line-cursor {
        stroke: $riseup_black;
        stroke-width: 2px;
      }

      .positive-bar {
        fill: $riseup_green;
      }

      .negative-bar {
        fill: $riseup_coral;
      }

      .positive-line {
        fill: $riseup_green;
        stroke: $riseup_green;
      }

      .negative-line {
        fill: $riseup_coral;
        stroke: $riseup_coral;
      }

      .caption-background {
        fill: white;
      }

      .empty-month-line-cursor {
        stroke: $riseup_gray_1;
        stroke-width: 1px;
      }

      .month-caption {
        font-size: 10px;
        fill: $riseup_black;
      }

      .positive-caption {
        fill: $riseup_green;
      }

      .zero-caption {
        fill: $riseup_mustard;
      }

      .negative-caption {
        fill: $riseup_coral;
      }

      .empty-month-caption {
        font-size: 10px;
        fill: $riseup_gray_1;
      }

      .bold-caption {
        font-weight: bold;
      }
    }
  }
</style>
